import { observable, action } from 'mobx';
import { db, firebase } from '../firebase';
import * as toastr from 'toastr';
import history from '../components/history';
import { v4 as uuidv4 } from 'uuid';
import * as moment from 'moment';

// =====================================
// CONSTS
import { Messages } from '../const/Messages';
import { OrderLabels } from '../const/OrderLabels';
import { Statuses } from '../const/Statuses';
import { ProductNotes } from '../const/ProductNotes';

// =====================================
// STORES
import { commonStore } from './CommonStore';

// =====================================
// MODELS
import { Order } from '../models/Order';
import { Article } from '../models/Article';
import { Customer } from '../models/Customer';
import { User } from '../models/User';
import { Location } from '../models/Location';

export interface IOrdersStore {
    customer: Customer;
    orders: Array<Order>;
    acceptedOrders: Array<Order>;
    productsForOrder: Array<Article>;
    orderRequestedDeliveryDate: any;
    orderDeliverAsSoonAsPossible: boolean;
    orderRequestedDeliveryLabel: string;
    orderLabel: string;
    locations: Array<Location>;
    locationCode: string;
    locationAddress: string;
    selectedRowId: string;
    productId: string;
    order: Order;

    isLoading: boolean;
    isOrderEmpty: boolean;
    isAcceptedOrderEmpty: boolean;
    isOrderListVisible: boolean;
    isArticlesPriceChangeVisible: boolean;
    isSaveOrderBtnDisabled: boolean;
    isModalVisible: boolean;
    isRequestedAmountDisabled: boolean;
    isEditOrder: boolean;

    getOrders(customerId: string): void;
    getAcceptedOrders(customerId: string): void;
    getCustomerInfo(customerId: string): void;
    getOrderById(orderId: string, isInboundOrder: boolean): void;
    getOrderByIdForEdit(customerId: string, orderId: string): void;
    getArticlesForOrder(customerId: string): void;
    handleChangeLocation(event: any): void;
    decreaseAmount(product: Article): void;
    increaseAmount(product: Article): void;
    enableRequestedAmountInput(event: any): void;
    handleRequestedAmount(product: Article): any;
    goToOrderDetails(): void;
    goBackToOrder(): void;
    handleChangeOrderRequestedDeliveryDate(data: any): void;
    handleChangeOrderDeliverAsSoonAsPossible(event: any): void;
    handleChangeOrderLabel(event: any): void;
    goToArticlesPriceChange(): void;
    goBackToOrderDetails(): void;
    showInputForPriceChange(product: Article): void;
    handleChangeAcquisitionPrice(event: any, id: any): void;
    handleShowModal(): void;
    handleCloseModal(): void;
    editFavouriteArticles(customerId: string): void;
    saveOrder(customerId: string, orderId: string): void;
    reset(): void;
}

export class OrdersStore implements IOrdersStore {
    constructor() {
        this.customer = new Customer();
        this.orders = [];
        this.acceptedOrders = [];
        this.productsForOrder = [];
        this.orderRequestedDeliveryDate = null;
        this.orderDeliverAsSoonAsPossible = true;
        this.orderRequestedDeliveryLabel = "Što pre";
        this.orderLabel = OrderLabels.V;
        this.locations = [{ externalReferenceCode: "0", name: "Molimo odaberite lokaciju" }];
        this.locationCode = "0";
        this.locationAddress = "";
        this.selectedRowId = "";
        this.productId = "";
        this.order = new Order();

        this.isLoading = true;
        this.isOrderEmpty = true;
        this.isAcceptedOrderEmpty = true;
        this.isOrderListVisible = false;
        this.isArticlesPriceChangeVisible = false;
        this.isSaveOrderBtnDisabled = false;
        this.isModalVisible = false;
        this.isRequestedAmountDisabled = false;
        this.isEditOrder = false;
    }

    @observable customer: Customer;
    @observable orders: Array<Order>;
    @observable acceptedOrders: Array<Order>;
    @observable productsForOrder: Array<Article>;
    @observable orderRequestedDeliveryDate: any;
    @observable orderDeliverAsSoonAsPossible: boolean;
    @observable orderRequestedDeliveryLabel: string;
    @observable orderLabel: string;
    @observable locations: Array<Location>;
    @observable locationCode: string;
    @observable locationAddress: string;
    @observable selectedRowId: string;
    @observable productId: string;
    @observable order: Order;

    @observable isLoading: boolean;
    @observable isOrderEmpty: boolean;
    @observable isAcceptedOrderEmpty: boolean;
    @observable isOrderListVisible: boolean;
    @observable isArticlesPriceChangeVisible: boolean;
    @observable isSaveOrderBtnDisabled: boolean;
    @observable isModalVisible: boolean;
    @observable isRequestedAmountDisabled: boolean;
    @observable isEditOrder: boolean;

    @action getOrders = async (customerId: string) => {
        try {
            let currentUser: User = commonStore.user;

            const docOrders = await db.collection("organizations")
                .doc(currentUser.orgId)
                .collection('inbound_orders')
                .where('customer.id', "==", customerId)
                .orderBy("createdAt", "desc")
                .limit(30)
                .get();

            if (!docOrders.empty) {
                docOrders.forEach((doc) => {
                    this.orders = [...this.orders, doc.data() as any];
                });
                this.isOrderEmpty = false;
            }

            const docCustomer = await db.collection("organizations")
                .doc(currentUser.orgId)
                .collection('customers')
                .doc(customerId)
                .get();

            this.customer = docCustomer.data() as any;
            this.isLoading = false;
        }
        catch (error) {
            this.isLoading = false;
            console.error("Error getting orders: ", error);
        };
    }

    @action getAcceptedOrders = async (customerId: string) => {
        try {
            let currentUser: User = commonStore.user;

            const docOrders = await db.collection("organizations")
                .doc(currentUser.orgId)
                .collection('inbound_orders')
                .where('customer.id', "==", customerId)
                .where('status.code', "==", Statuses.INVOICED)
                .orderBy("createdAt", "desc")
                .limit(30)
                .get();

            if (!docOrders.empty) {
                docOrders.forEach((doc) => {
                    this.acceptedOrders = [...this.acceptedOrders, doc.data() as any];
                });
                this.isAcceptedOrderEmpty = false;
            }

            const docCustomer = await db.collection("organizations")
                .doc(currentUser.orgId)
                .collection('customers')
                .doc(customerId)
                .get();

            this.customer = docCustomer.data() as any;
            this.isLoading = false;
        }
        catch (error) {
            this.isLoading = false;
            console.error("Error getting orders: ", error);
        };
    }

    @action getCustomerInfo = async (customerId: string) => {
        try {
            let currentUser: User = commonStore.user;

            const docCustomer = await db.collection("organizations")
                .doc(currentUser.orgId)
                .collection('customers')
                .doc(customerId)
                .get();

            this.customer = docCustomer.data() as any;
            this.isLoading = false;
        }
        catch (error) {
            this.isLoading = false;
            console.error("Error getting customer info: ", error);
        };
    }

    @action getOrderById = async (orderId: string, getOrderById: boolean) => {
        try {
            let currentUser: User = commonStore.user;
            let collectionName = "";
            getOrderById ? collectionName = 'inbound_orders' : collectionName = 'inbound_orders_erp_integrated'

            const doc = await db.collection("organizations")
                .doc(currentUser.orgId)
                .collection(collectionName)
                .doc(orderId)
                .get();

            this.order = doc.data() as any;

            this.productsForOrder.forEach(product => {
                if (doc.data().products.length > 0) {
                    doc.data().products.forEach(item => {
                        if (product.id === item.id) {
                            product.productOrderedAmount = item.orderedAmount;
                            product.note = item.note;
                        }
                    })
                }
            });

            (this.productsForOrder as any).replace(this.productsForOrder);
            let locationCode = this.locations.filter((item) => item.name === doc.data().customer.deliveryLocation.fullAddressText);
            if (locationCode.length > 0) {
                this.locationCode = locationCode[0].externalReferenceCode;
            }
            this.locationAddress = doc.data().customer.deliveryLocation.fullAddressText;
            this.orderRequestedDeliveryDate = moment(doc.data().requestedDeliveryDate.toDate());
            this.orderDeliverAsSoonAsPossible = doc.data().deliverAsSoonAsPossible;
            this.orderLabel = doc.data().labels[0];
            this.isEditOrder = true;

            this.isLoading = false;
        }
        catch (error) {
            this.isLoading = false;
            console.error("Error getting order details: ", error);
        };
    }

    @action getOrderByIdForEdit = async (customerId: string, orderId: string) => {
        await this.getArticlesForOrder(customerId);
        await this.getOrderById(orderId, true);
    }

    @action getArticlesForOrder = async (customerId: string) => {
        try {
            let currentUser: User = commonStore.user;
            this.orderRequestedDeliveryDate = moment().add(1, 'days');

            const docCustomer = await db.collection("organizations")
                .doc(currentUser.orgId)
                .collection('customers')
                .doc(customerId)
                .get();

            this.customer = docCustomer.data() as any;
            if (docCustomer.data().locations !== null) {
                let defaultLocation: Location = {
                    externalReferenceCode: docCustomer.data().externalReferenceCode,
                    name: docCustomer.data().companyAddress
                };
                let locations = [defaultLocation];

                if (docCustomer.data().locations.length > 0) {
                    docCustomer.data().locations.forEach(item => {
                        let location = {
                            externalReferenceCode: item.externalReferenceCode,
                            name: `${item.address}, ${item.location}`
                        }
                        locations.push(location)
                    })
                }

                (this.locations as any).replace([...this.locations, ...locations]);
            }

            const docFavouriteAarticles = await db.collection("organizations")
                .doc(currentUser.orgId)
                .collection('customers')
                .doc(customerId)
                .collection('favourite_articles')
                .get();

            if (!docFavouriteAarticles.empty) {
                let articlesForOrder = [];
                docFavouriteAarticles.forEach(async (doc) => {
                    doc.data().articles.forEach(async (item) => {
                        const querySnapshotArticles = await db.collection('catalogs')
                            .doc(currentUser.orgId)
                            .collection('articles')
                            .doc(item.id)
                            .get();

                        articlesForOrder = [...articlesForOrder, querySnapshotArticles.data()];

                        let productForOrder: Article = new Article();
                        articlesForOrder.forEach((article: Article) => {
                            productForOrder.VAT = article.VAT;
                            productForOrder.acquisitionPrice = article.acquisitionPrice;
                            productForOrder.code = article.code;
                            productForOrder.id = article.id;
                            productForOrder.name = article.name;
                            productForOrder.unitOfMeasurement = article.unitOfMeasurement;
                            productForOrder.customSalesPrice = item.customSalesPrice !== 0 ? item.customSalesPrice.toString() : "";
                            productForOrder.copyCustomSalesPrice = item.customSalesPrice !== 0 ? item.customSalesPrice.toString() : "";
                            productForOrder.productMaxOrderQuantity = item.productMaxOrderQuantity;
                            productForOrder.productMinOrderQuantity = item.productMinOrderQuantity;
                            //productForOrder.productTransportPackageSize = item.productTransportPackageSize;
                            productForOrder.stepDown = item.stepDown;
                            productForOrder.stepUp = item.stepUp;
                        });
                        this.productsForOrder.push(productForOrder);
                        localStorage.setItem('favoriteArticles', JSON.stringify([...this.productsForOrder]));
                    });
                });
                this.isLoading = false;
            } else {
                this.isLoading = false;
                localStorage.removeItem("favoriteArticles");
            }
        }
        catch (error) {
            this.isLoading = false;
            console.error("Error getting articles: ", error);
        };
    }

    @action handleChangeLocation = (event: any): void => {
        this.locationCode = event.target.value;

        let name: string = this.locations.filter((item) => item.externalReferenceCode === event.target.value)[0].name;
        this.locationAddress = name;
    }

    @action decreaseAmount = (product: Article): void => {
        if (product.productOrderedAmount > 0) {
            product.productOrderedAmount = product.productOrderedAmount - product.stepDown;
            this.addNoteToOrderProduct();
        }
    }

    @action increaseAmount = (product: Article): void => {
        if (product.productOrderedAmount <= product.productMaxOrderQuantity) {
            product.productOrderedAmount = product.productOrderedAmount + product.stepUp;
            this.addNoteToOrderProduct();
        }
    }

    @action enableRequestedAmountInput = (event: any) => {
        //this.isRequestedAmountDisabled = !this.isRequestedAmountDisabled;
        this.productId = event.target.name;
        this.productsForOrder.map((product: Article) => {
            if (this.productId === product.id) {
                product.isReadOnly = !product.isReadOnly;
            }
            return product;
        })
    }

    @action handleRequestedAmount = (product: Article) => (event: any) => {
        product.productOrderedAmount = +event.target.value;
        this.addNoteToOrderProduct();
    }

    @action validateOrder = (): boolean => {
        let isValid: boolean = true;

        let emptyRequestedAmounts = this.productsForOrder.filter((product) => product.productOrderedAmount !== 0).length > 0;
        if (!emptyRequestedAmounts) {
            isValid = false;
            toastr.error(Messages.NO_PRODUCTS_ORDERED);
        }

        return isValid;
    }

    @action addNoteToOrderProduct = () => {
        if (!this.isEditOrder) return;

        //add notes to modified product for order
        this.productsForOrder.forEach(productForOrder => {
            if (productForOrder.note === ProductNotes.replaced) return;

            //add added note
            const isAdded = productForOrder.productOrderedAmount > 0 && !(this.order.products.filter(product => product.id === productForOrder.id).length > 0)
            if (isAdded) productForOrder.note = ProductNotes.added;

            //add modified note
            const isModified = this.order.products.find(product => product.id === productForOrder.id && productForOrder.productOrderedAmount !== product.orderedAmount);
            if (isModified) productForOrder.note = ProductNotes.modified;

            //add canceled note
            const isCanceled = this.order.products.find(product => product.id === productForOrder.id && productForOrder.productOrderedAmount === 0);
            if (isCanceled) productForOrder.note = ProductNotes.canceled;

            //add empty string if values are same
            const isSameValueAlreadySaved = this.order.products.find(product => product.id === productForOrder.id && productForOrder.productOrderedAmount === product.orderedAmount);
            const isSameValueAdded = productForOrder.productOrderedAmount === 0 && !(this.order.products.filter(product => product.id === productForOrder.id).length > 0)
            if (isSameValueAlreadySaved || isSameValueAdded) productForOrder.note = "";
        });
    }

    @action goToOrderDetails = () => {
        if (!this.validateOrder()) return;

        //reset all notes exept replaced || replacement
        this.productsForOrder.forEach(product => {
            if (!(product.note === ProductNotes.replaced || product.note === ProductNotes.replacement)) {
                product.note = '';
            }
        });

        this.addNoteToOrderProduct();

        this.isOrderListVisible = true;
        this.isArticlesPriceChangeVisible = false;
    }

    @action goBackToOrder = () => {
        this.isOrderListVisible = false;
        // this.orderRequestedDeliveryDate = moment().add(1, 'days');
        // this.orderDeliverAsSoonAsPossible = true;
        // this.orderRequestedDeliveryLabel = "Što pre";
    }

    @action handleChangeOrderRequestedDeliveryDate = (date: any): void => {
        this.orderRequestedDeliveryDate = date;
        this.orderRequestedDeliveryLabel = moment(date).format("DD.MM.YYYY")
    }

    @action handleChangeOrderDeliverAsSoonAsPossible = (event): void => {
        this.orderDeliverAsSoonAsPossible = event.target.checked;
        if (event.target.checked) {
            this.orderRequestedDeliveryLabel = "Što pre";
        } else {
            if (this.orderRequestedDeliveryDate !== null) {
                this.orderRequestedDeliveryLabel = moment(this.orderRequestedDeliveryDate).format("DD.MM.YYYY");
            } else {
                this.orderRequestedDeliveryLabel = "";
            }
        }
    }

    @action handleChangeOrderLabel = (event: any): void => {
        this.orderLabel = event.target.value;
    }

    @action goToArticlesPriceChange = (): void => {
        this.isOrderListVisible = false;
        this.isArticlesPriceChangeVisible = true;
    }

    @action goBackToOrderDetails = () => {
        this.selectedRowId = "";
        this.isModalVisible = false;
        this.isOrderListVisible = true;
        this.isArticlesPriceChangeVisible = false;

        this.productsForOrder.forEach((item) => {
            item.customSalesPrice = item.copyCustomSalesPrice;
        });
    }

    @action showInputForPriceChange = (product: Article) => {
        this.selectedRowId = product.id;
    }

    @action handleChangeAcquisitionPrice = (event: any, id: any): void => {
        const { value } = event.target;
        let curentArticle = this.productsForOrder.filter(article => article.id === id)[0];
        curentArticle.customSalesPrice = value;
    }

    @action handleShowModal = () => {
        let changedFavoriteArticles = this.productsForOrder.filter(article => article.customSalesPrice !== article.copyCustomSalesPrice);
        if (changedFavoriteArticles.length > 0) {
            this.isModalVisible = true;
        } else {
            this.goBackToOrderDetails();
        }
    }

    @action handleCloseModal = () => {
        this.isModalVisible = false;
    }

    @action editFavouriteArticles = async (customerId: string) => {
        try {
            let currentUser: User = commonStore.user;
            let id: string = uuidv4();

            const docFavouriteAarticles = await db.collection("organizations")
                .doc(currentUser.orgId)
                .collection('customers')
                .doc(customerId)
                .collection('favourite_articles')
                .get();

            let favouriteArticleDocumentId: string = "";
            docFavouriteAarticles.forEach(async (doc) => {
                favouriteArticleDocumentId = doc.data().id;
            });

            if (favouriteArticleDocumentId !== "") {
                await db.collection("organizations")
                    .doc(currentUser.orgId)
                    .collection("customers")
                    .doc(customerId)
                    .collection("favourite_articles")
                    .doc(favouriteArticleDocumentId)
                    .delete();
            }

            let articles = [];
            this.productsForOrder.forEach((item) => {
                item.copyCustomSalesPrice = item.customSalesPrice;

                let article = {
                    id: item.id,
                    customSalesPrice: item.customSalesPrice !== "" ? +item.customSalesPrice : 0,
                    code: item.code !== "" ? item.code : 9999,
                    productMaxOrderQuantity: item.productMaxOrderQuantity !== 0 ? item.productMaxOrderQuantity : 999999,
                    productMinOrderQuantity: item.productMinOrderQuantity !== 0 ? item.productMinOrderQuantity : 0,
                    //productTransportPackageSize: item.productTransportPackageSize !== 0 ? item.productTransportPackageSize : 1,
                    stepDown: item.stepDown !== 0 ? item.stepDown : 1,
                    stepUp: item.stepUp !== 0 ? item.stepUp : 5
                };

                articles.push(article);
            });

            await db.collection("organizations")
                .doc(currentUser.orgId)
                .collection("customers")
                .doc(customerId)
                .collection("favourite_articles")
                .doc(id)
                .set({
                    id: id,
                    articles: articles
                });

            toastr.success(Messages.CHANGES_SAVED);
            this.isOrderListVisible = true;
            this.isArticlesPriceChangeVisible = false;
        }
        catch (error) {
            console.error("Error saving document: ", error);
        };
    }

    @action validateDeliveryDate = (): boolean => {
        let isValid: boolean = true;

        if (this.orderRequestedDeliveryDate === null && this.orderDeliverAsSoonAsPossible === false) {
            isValid = false;
            toastr.error(Messages.NO_DELIVERY_DATE);
        }

        return isValid;
    }

    @action saveOrder = async (customerId: string, orderId: string) => {
        if (!this.validateDeliveryDate()) return;

        try {
            this.isSaveOrderBtnDisabled = true;
            let id: string = uuidv4();
            let currentUser: User = commonStore.user;
            let date = new Date();

            const docCustomer = await db.collection("organizations")
                .doc(currentUser.orgId)
                .collection('customers')
                .doc(customerId)
                .get();

            let customer: Customer = docCustomer.data() as any;

            let articlesForOrder = [];
            this.productsForOrder.forEach((product) => {
                if (product.productOrderedAmount > 0 || product.note.length > 0) {
                    let article = {
                        id: product.id,
                        categoryId: "",
                        categoryName: "",
                        code: product.code,
                        unitOfMeasurement: product.unitOfMeasurement,
                        name: product.name,
                        orderedAmount: product.productOrderedAmount,
                        offeredAmount: product.productOfferedAmount,
                        sentAmount: product.productOfferedAmount,
                        receivedAmount: product.productOfferedAmount,
                        offeredDeliveryDate: product.productOfferedDeliveryDate,
                        realizedSalesPrice: +product.customSalesPrice,
                        standardSalesPrice: product.productOfferedAmount,
                        note: product.note
                    };

                    articlesForOrder.push(article);
                }
            });

            let orderDeliverAsSoonAsPossible: boolean = true;
            let orderRequestedDeliveryDate: any = null;

            if (this.orderDeliverAsSoonAsPossible) {
                orderDeliverAsSoonAsPossible = true;
                orderRequestedDeliveryDate = this.orderRequestedDeliveryDate !== null ? firebase.firestore.Timestamp.fromDate(this.orderRequestedDeliveryDate.startOf('day').toDate()) : firebase.firestore.Timestamp.fromDate(moment().startOf('day').toDate());
            } else {
                orderDeliverAsSoonAsPossible = false;
                orderRequestedDeliveryDate = firebase.firestore.Timestamp.fromDate(this.orderRequestedDeliveryDate.startOf('day').toDate());
            }

            let externalReferenceCode: string = "";
            let deliveryLocationName: string = "";

            if (this.locationCode !== "0" && this.locationCode !== customer.externalReferenceCode) {
                externalReferenceCode = this.locationCode;
                let name: string = this.locations.filter((item) => item.externalReferenceCode === this.locationCode)[0].name;
                deliveryLocationName = name;
            } else {
                externalReferenceCode = customer.externalReferenceCode;
                deliveryLocationName = customer.companyAddress;
            }

            if (!this.isEditOrder) {
                //Save new order
                const orderCounter = await db.collection('organizations')
                    .doc(currentUser.orgId)
                    .collection('customer_order_counter')
                    .doc(customerId)
                    .get();

                let counter: number = 1;
                if (orderCounter.data() !== undefined) {
                    counter = orderCounter.data().counter
                }

                let formatOrderCounter: string = commonStore.paddy(counter, 6);

                await db.collection("organizations")
                    .doc(currentUser.orgId)
                    .collection('inbound_orders')
                    .doc(id)
                    .set({
                        id: id,
                        number: formatOrderCounter,
                        status: {
                            code: Statuses.PENDING,
                            message: "",
                            triggeredByUserId: currentUser.id,
                            triggeredAtDate: firebase.firestore.Timestamp.fromDate(date)
                        },
                        ERPOrderReferenceCode: "",
                        requestedDeliveryDate: orderRequestedDeliveryDate,
                        offeredDeliveryDate: "",
                        deliverAsSoonAsPossible: orderDeliverAsSoonAsPossible,
                        products: articlesForOrder,
                        labels: [this.orderLabel],
                        notes: [],
                        customer: {
                            id: customer.id,
                            name: customer.fullName,
                            taxId: customer.taxId,
                            deliveryLocation: {
                                id: "",
                                name: "",
                                fullAddressText: deliveryLocationName
                            },
                            contactPersons: [
                                {
                                    salutation: "",
                                    name: "",
                                    phone: "",
                                    email: "",
                                    function: {
                                        id: "",
                                        name: ""
                                    },
                                    description: ""
                                }
                            ],
                            ERPDeliveryLocationReferenceCode: externalReferenceCode,
                            ERPCustomerReferenceCode: customer.externalReferenceCode
                        },
                        supplier: {
                            id: currentUser.orgId
                        },
                        createdByUserID: currentUser.id,
                        isMarkedAsArchived: false,
                        createdAt: firebase.firestore.Timestamp.fromDate(date),
                        lastModifiedAt: firebase.firestore.Timestamp.fromDate(date),
                        version: 1
                    });

                await db.collection('organizations')
                    .doc(currentUser.orgId)
                    .collection('customer_order_counter')
                    .doc(customerId)
                    .set({
                        counter: counter + 1
                    });

                toastr.success(Messages.SUCCESS_ORDER);
                history.push(`/orders/${customerId}`);
            } else {
                //Edit order
                await db.collection("organizations")
                    .doc(currentUser.orgId)
                    .collection("inbound_orders")
                    .doc(orderId)
                    .update({
                        status: {
                            code: Statuses.MODIFIED,
                            message: "",
                            triggeredByUserId: currentUser.id,
                            triggeredAtDate: firebase.firestore.Timestamp.fromDate(date)
                        },
                        requestedDeliveryDate: orderRequestedDeliveryDate,
                        deliverAsSoonAsPossible: orderDeliverAsSoonAsPossible,
                        products: articlesForOrder,
                        labels: [this.orderLabel],
                        customer: {
                            id: customer.id,
                            name: customer.fullName,
                            taxId: customer.taxId,
                            deliveryLocation: {
                                id: "",
                                name: "",
                                fullAddressText: deliveryLocationName
                            },
                            contactPersons: [
                                {
                                    salutation: "",
                                    name: "",
                                    phone: "",
                                    email: "",
                                    function: {
                                        id: "",
                                        name: ""
                                    },
                                    description: ""
                                }
                            ],
                            ERPDeliveryLocationReferenceCode: externalReferenceCode,
                            ERPCustomerReferenceCode: customer.externalReferenceCode
                        },
                        lastModifiedAt: firebase.firestore.Timestamp.fromDate(date),
                        version: this.order.version + 1
                    });

                await db.collection("organizations")
                    .doc(currentUser.orgId)
                    .collection('inbound_orders')
                    .doc(this.order.id)
                    .collection('change_log')
                    .doc(id)
                    .set({
                        id: this.order.id,
                        number: this.order.number,
                        status: {
                            code: Statuses.MODIFIED,
                            message: "",
                            triggeredByUserId: currentUser.id,
                            triggeredAtDate: firebase.firestore.Timestamp.fromDate(date)
                        },
                        ERPOrderReferenceCode: "",
                        requestedDeliveryDate: orderRequestedDeliveryDate,
                        offeredDeliveryDate: "",
                        deliverAsSoonAsPossible: orderDeliverAsSoonAsPossible,
                        products: articlesForOrder,
                        labels: [this.orderLabel],
                        notes: [],
                        customer: {
                            id: customer.id,
                            name: customer.fullName,
                            taxId: customer.taxId,
                            deliveryLocation: {
                                id: "",
                                name: "",
                                fullAddressText: deliveryLocationName
                            },
                            contactPersons: [
                                {
                                    salutation: "",
                                    name: "",
                                    phone: "",
                                    email: "",
                                    function: {
                                        id: "",
                                        name: ""
                                    },
                                    description: ""
                                }
                            ],
                            ERPDeliveryLocationReferenceCode: externalReferenceCode,
                            ERPCustomerReferenceCode: customer.externalReferenceCode
                        },
                        supplier: {
                            id: this.order.supplier.id
                        },
                        createdByUserID: this.order.createdByUserID,
                        isMarkedAsArchived: false,
                        createdAt: firebase.firestore.Timestamp.fromDate(date),
                        lastModifiedAt: firebase.firestore.Timestamp.fromDate(date),
                        version: this.order.version + 1
                    });


                toastr.success(Messages.SUCCESS_ORDER_MODIFIED);
                history.push(`/orders/${customerId}`);
            }
        }
        catch (error) {
            this.isSaveOrderBtnDisabled = false;
            console.error("Error saving document: ", error);
        };
    }

    @action reset = (): void => {
        this.customer = new Customer();
        this.orders = [];
        this.acceptedOrders = [];
        this.productsForOrder = [];
        this.orderRequestedDeliveryDate = null;
        this.orderDeliverAsSoonAsPossible = true;
        this.orderRequestedDeliveryLabel = "Što pre";
        this.orderLabel = OrderLabels.V;
        this.locations = [{ externalReferenceCode: "0", name: "Molimo odaberite lokaciju" }];
        this.locationCode = "0";
        this.locationAddress = "";
        this.selectedRowId = "";
        this.productId = "";
        this.order = new Order();

        this.isLoading = true;
        this.isOrderEmpty = true;
        this.isAcceptedOrderEmpty = true;
        this.isOrderListVisible = false;
        this.isArticlesPriceChangeVisible = false;
        this.isSaveOrderBtnDisabled = false;
        this.isModalVisible = false;
        this.isRequestedAmountDisabled = false;
        this.isEditOrder = false;
    }
}

export let ordersStore = new OrdersStore();